import { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";

const API = "https://lbtracker.aliaaa.fr/api";

async function apiFetch(path) {
  const res = await fetch(`${API}${path}`);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// ─── Hook responsive ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── Themes ──────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg:          "#0d1117",
    surface:     "#161b22",
    surface2:    "#1c2230",
    border:      "#21262d",
    borderHover: "#30363d",
    text:        "#e6edf3",
    textMuted:   "#7d8590",
    textFaint:   "#30363d",
    accent:      "#00e5a0",
    accentDim:   "rgba(0,229,160,0.12)",
    accentText:  "#0d1117",
    danger:      "#f85149",
    dangerBg:    "rgba(248,81,73,0.12)",
    success:     "#3fb950",
    chart:       "#21262d",
    shadow:      "0 1px 3px rgba(0,0,0,0.4)",
    shadowMd:    "0 4px 16px rgba(0,0,0,0.5)",
  },
  light: {
    bg:          "#f6f8fa",
    surface:     "#ffffff",
    surface2:    "#f0f3f6",
    border:      "#d0d7de",
    borderHover: "#b0bbc5",
    text:        "#1f2328",
    textMuted:   "#656d76",
    textFaint:   "#d0d7de",
    accent:      "#0e8a5f",
    accentDim:   "rgba(14,138,95,0.1)",
    accentText:  "#ffffff",
    danger:      "#d1242f",
    dangerBg:    "rgba(209,36,47,0.08)",
    success:     "#1a7f37",
    chart:       "#e8ecef",
    shadow:      "0 1px 3px rgba(0,0,0,0.08)",
    shadowMd:    "0 4px 16px rgba(0,0,0,0.12)",
  },
};

// ─── Périodes ────────────────────────────────────────────────────────────────
const DAY = 24 * 3600 * 1000;
const RANGES = [
  { key: "1d", label: "1J", ms: DAY },
  { key: "1w", label: "1S", ms: 7 * DAY },
  { key: "1m", label: "1M", ms: 30 * DAY },
  { key: "1y", label: "1A", ms: 365 * DAY },
];

const pad = n => String(n).padStart(2, "0");

// Label compact pour l'axe X, adapté à la période sélectionnée
function fmtAxis(ts, range) {
  const d = new Date(ts);
  if (range === "1d") return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (range === "1w") return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}h`;
  if (range === "1y") return `${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`; // 1m
}

// Horodatage complet pour le tooltip
function fmtFull(ts) {
  const d = new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Filtre l'historique sur la période + prépare les libellés
function prepareSeries(history, range) {
  const rangeMs = RANGES.find(r => r.key === range)?.ms ?? 30 * DAY;
  const cutoff = Date.now() - rangeMs;
  return (history || [])
    .filter(h => h.ts == null || new Date(h.ts).getTime() >= cutoff)
    .map(h => ({
      ...h,
      date: h.ts ? fmtAxis(h.ts, range) : h.date,
      full: h.ts ? fmtFull(h.ts) : h.date,
    }));
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label, t }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: t.surface, border: `1px solid ${t.border}`,
      borderRadius: 8, padding: "8px 14px", boxShadow: t.shadowMd,
      fontFamily: "ui-monospace, monospace", fontSize: 12,
    }}>
      <p style={{ color: t.textMuted, margin: "0 0 4px" }}>{payload[0].payload?.full || label}</p>
      <p style={{ color: t.accent, margin: 0, fontWeight: 700 }}>
        {payload[0].value.toFixed(2)} €
      </p>
    </div>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────────────
function Sparkline({ history, t }) {
  if (!history?.length) return <div style={{ height: 36 }} />;
  const prices = history.map(h => h.price);
  const min    = Math.min(...prices);
  const max    = Math.max(...prices);
  const trend  = prices[prices.length - 1] - prices[0];
  return (
    <ResponsiveContainer width="100%" height={36}>
      <LineChart data={history}>
        <Line type="monotone" dataKey="price"
          stroke={trend <= 0 ? t.success : t.danger}
          strokeWidth={1.5} dot={false}
        />
        <YAxis domain={[min * 0.96, max * 1.04]} hide />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── PriceCard ────────────────────────────────────────────────────────────────
function PriceCard({ item, history, onClick, selected, t }) {
  const latest  = item.latest_price;
  const prev    = history?.[history.length - 2]?.price;
  const delta   = prev ? latest - prev : 0;
  const pct     = prev ? ((delta / prev) * 100).toFixed(1) : null;
  const saving  = item.off_price ? ((1 - latest / item.off_price) * 100).toFixed(0) : null;
  const inactive = item.active === false;

  return (
    <div
      onClick={() => onClick(item)}
      style={{
        background:   selected ? t.accentDim : t.surface,
        border:       `1px solid ${selected ? t.accent : t.border}`,
        borderRadius: 10, padding: "14px", cursor: "pointer",
        transition:   "border-color 0.15s, background 0.15s, box-shadow 0.15s",
        boxShadow:    selected ? `0 0 0 1px ${t.accent}` : t.shadow,
        position:     "relative",
        opacity:      inactive ? 0.4 : (item.tradable ? 1 : 0.55),
        filter:       inactive ? "grayscale(1)" : "none",
      }}
    >
      {inactive && (
        <span style={{
          position: "absolute", top: 8, right: 8,
          background: t.surface2, color: t.textMuted,
          fontSize: 9, padding: "2px 6px", borderRadius: 4,
          fontFamily: "monospace", letterSpacing: 0.8, fontWeight: 700,
          border: `1px solid ${t.border}`,
        }}>RETIRÉ</span>
      )}
      {!inactive && !item.tradable && (
        <span style={{
          position: "absolute", top: 8, right: 8,
          background: t.dangerBg, color: t.danger,
          fontSize: 9, padding: "2px 6px", borderRadius: 4,
          fontFamily: "monospace", letterSpacing: 0.8, fontWeight: 700,
        }}>INDISPO</span>
      )}

      {item.icon && (
        <div style={{
          width: "100%", height: 90, borderRadius: 8, marginBottom: 10,
          background: t.surface2, border: `1px solid ${t.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <img src={item.icon} alt=""
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }}
          />
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: t.text, lineHeight: 1.35, marginBottom: 2 }}>
          {item.name}
        </div>
        {saving && (
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: t.success, fontFamily: "monospace" }}>
            −{saving}% vs officiel
          </span>
        )}
      </div>

      {history === undefined
        ? <div style={{ height: 36, display: "flex", alignItems: "center" }}>
            <span style={{ color: t.textFaint, fontSize: 10, fontFamily: "monospace" }}>…</span>
          </div>
        : <Sparkline history={history} t={t} />
      }

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: t.accent, fontFamily: "ui-monospace, monospace" }}>
          {latest?.toFixed(2) ?? "—"} €
        </span>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
          {pct && (
            <span style={{ fontSize: 10, fontFamily: "monospace", fontWeight: 600, color: delta >= 0 ? t.danger : t.success }}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(pct)}%
            </span>
          )}
          {item.off_price && (
            <span style={{ fontSize: 9, color: t.textMuted, fontFamily: "monospace", textDecoration: "line-through" }}>
              {item.off_price.toFixed(2)} €
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DetailChart ──────────────────────────────────────────────────────────────
function DetailChart({ item, history, loading, t, isMobile, onBack, range, setRange }) {
  const placeholder = (msg) => (
    <div style={{
      background: t.surface, border: `1px solid ${t.border}`,
      borderRadius: 12, height: isMobile ? 260 : 400,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: t.shadow,
    }}>
      <span style={{ color: t.textMuted, fontFamily: "monospace", fontSize: 12, letterSpacing: 1 }}>{msg}</span>
    </div>
  );

  if (loading || history === undefined) return placeholder("Chargement…");
  if (!history?.length)                 return placeholder("Aucun historique disponible");

  const data   = prepareSeries(history, range);
  const prices = data.map(h => h.price);
  const min    = prices.length ? Math.min(...prices) : 0;
  const max    = prices.length ? Math.max(...prices) : 0;
  const latest = prices.length ? prices[prices.length - 1] : (item.latest_price ?? 0);
  const saving = item.off_price && latest ? ((1 - latest / item.off_price) * 100).toFixed(1) : null;

  return (
    <div style={{
      background: t.surface, border: `1px solid ${t.border}`,
      borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: t.shadow,
    }}>
      {/* Bouton retour mobile */}
      {isMobile && onBack && (
        <button onClick={onBack} style={{
          background: "transparent", border: "none", cursor: "pointer",
          color: t.textMuted, fontSize: 13, padding: "0 0 14px 0",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          ← Retour
        </button>
      )}

      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          {item.icon && (
            <img src={item.icon} alt="" style={{
              width: isMobile ? 40 : 52, height: isMobile ? 40 : 52,
              borderRadius: 10, objectFit: "contain", flexShrink: 0,
              background: t.surface2, border: `1px solid ${t.border}`,
            }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", letterSpacing: 1, marginBottom: 4 }}>
              HISTORIQUE · {data.length} RELEVÉS
            </div>
            <div style={{ fontSize: isMobile ? 13 : 16, fontWeight: 700, color: t.text, lineHeight: 1.3 }}>
              {item.name}
            </div>
          </div>
        </div>
        {item.off_price && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", marginBottom: 2 }}>OFFICIEL</div>
            <div style={{ fontSize: 12, color: t.textMuted, textDecoration: "line-through", fontFamily: "monospace" }}>
              {item.off_price.toFixed(2)} €
            </div>
            {saving && (
              <div style={{ fontSize: 11, color: t.success, fontFamily: "monospace", fontWeight: 700 }}>
                −{saving}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sélecteur de période */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {RANGES.map(r => {
          const on = range === r.key;
          return (
            <button key={r.key} onClick={() => setRange(r.key)} style={{
              background:   on ? t.accent     : "transparent",
              color:        on ? t.accentText : t.textMuted,
              border:      `1px solid ${on ? t.accent : t.border}`,
              borderRadius: 6, padding: "4px 12px",
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              fontFamily: "monospace", letterSpacing: 0.5, transition: "all 0.15s",
            }}>{r.label}</button>
          );
        })}
      </div>

      {/* Chart */}
      {data.length === 0 ? (
        <div style={{
          height: isMobile ? 160 : 220, display: "flex", alignItems: "center", justifyContent: "center",
          color: t.textMuted, fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
        }}>
          Aucun relevé sur cette période
        </div>
      ) : (
      <ResponsiveContainer width="100%" height={isMobile ? 160 : 220}>
        <LineChart data={data} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.chart} vertical={false} />
          <XAxis dataKey="date"
            tick={{ fill: t.textMuted, fontSize: isMobile ? 9 : 10, fontFamily: "monospace" }}
            axisLine={false} tickLine={false}
            minTickGap={isMobile ? 28 : 40}
          />
          <YAxis
            domain={[min * 0.96, max * 1.04]}
            tick={{ fill: t.textMuted, fontSize: isMobile ? 9 : 10, fontFamily: "monospace" }}
            axisLine={false} tickLine={false}
            tickFormatter={v => `${v.toFixed(1)}€`}
          />
          <Tooltip content={<CustomTooltip t={t} />} />
          {item.off_price && (
            <ReferenceLine y={item.off_price} stroke={t.borderHover} strokeDasharray="4 4"
              label={{ value: "off.", fill: t.textMuted, fontSize: 9, fontFamily: "monospace", position: "right" }}
            />
          )}
          <Line type="monotone" dataKey="price" stroke={t.accent} strokeWidth={2}
            dot={data.length > 60 ? false : { fill: t.accent, r: 3 }} activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
      )}

      {/* Stats */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
        gap: 8, marginTop: 16,
      }}>
        {[
          { label: "MIN",    value: `${min.toFixed(2)} €`,    color: t.success   },
          { label: "MAX",    value: `${max.toFixed(2)} €`,    color: t.danger    },
          { label: "ACTUEL", value: `${latest.toFixed(2)} €`, color: t.accent    },
          { label: "LIMITE", value: item.limit ?? "∞",         color: t.textMuted },
        ].map(stat => (
          <div key={stat.label} style={{
            background: t.surface2, borderRadius: 8,
            padding: isMobile ? "8px 10px" : "10px 12px",
            border: `1px solid ${t.border}`,
          }}>
            <div style={{ fontSize: 9, color: t.textMuted, fontFamily: "monospace", letterSpacing: 0.8, marginBottom: 4 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: stat.color, fontFamily: "ui-monospace, monospace" }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ThemeToggle ──────────────────────────────────────────────────────────────
function ThemeToggle({ mode, onToggle, t }) {
  return (
    <button onClick={onToggle}
      title={mode === "dark" ? "Mode clair" : "Mode sombre"}
      style={{
        background: t.surface2, border: `1px solid ${t.border}`,
        borderRadius: 8, padding: "6px 10px", cursor: "pointer",
        color: t.textMuted, fontSize: 15, lineHeight: 1,
        transition: "all 0.15s", flexShrink: 0,
      }}
    >
      {mode === "dark" ? "☀" : "☾"}
    </button>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [mode, setMode] = useState(() => localStorage.getItem("theme") || "dark");
  const t        = THEMES[mode];
  const isMobile = useIsMobile();

  const [games,          setGames]         = useState([]);
  const [selectedGame,   setSelectedGame]  = useState(null);
  const [items,          setItems]         = useState([]);
  const [selectedItem,   setSelectedItem]  = useState(null);
  const [histories,      setHistories]     = useState({});
  const [loadingItems,   setLoadingItems]  = useState(false);
  const [loadingHistory, setLoadingHistory]= useState(false);
  const [error,          setError]         = useState(null);
  const [gameSearch,     setGameSearch]    = useState("");
  const [showEmpty,      setShowEmpty]     = useState(false);
  const [range,          setRange]         = useState("1m");

  const toggleMode = useCallback(() => {
    setMode(m => {
      const next = m === "dark" ? "light" : "dark";
      localStorage.setItem("theme", next);
      return next;
    });
  }, []);

  useEffect(() => {
    apiFetch("/games")
      .then(data => { setGames(data); if (data.length) setSelectedGame(data[0]); })
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (!selectedGame) return;
    setLoadingItems(true);
    setItems([]); setSelectedItem(null); setHistories({});
    apiFetch(`/items/${selectedGame.id}`)
      .then(data => { setItems(data); setSelectedItem(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoadingItems(false));
  }, [selectedGame]);

  useEffect(() => {
    if (!selectedItem || histories[selectedItem.id] !== undefined) return;
    setLoadingHistory(true);
    apiFetch(`/history/${selectedItem.id}`)
      .then(data => setHistories(prev => ({ ...prev, [selectedItem.id]: data })))
      .catch(() => setHistories(prev => ({ ...prev, [selectedItem.id]: [] })))
      .finally(() => setLoadingHistory(false));
  }, [selectedItem]); // eslint-disable-line

  useEffect(() => {
    if (!items.length) return;
    items.forEach(item => {
      if (histories[item.id] !== undefined) return;
      apiFetch(`/history/${item.id}`)
        .then(data => setHistories(prev => ({ ...prev, [item.id]: data })))
        .catch(() => setHistories(prev => ({ ...prev, [item.id]: [] })));
    });
  }, [items]); // eslint-disable-line

  const handleSelectItem = useCallback((item) => setSelectedItem(item), []);
  const handleBack       = useCallback(() => setSelectedItem(null), []);

  return (
    <div style={{
      minHeight: "100vh", background: t.bg, color: t.text,
      fontFamily: "-apple-system, 'Segoe UI', system-ui, sans-serif",
      transition: "background 0.2s, color 0.2s",
    }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap"
        rel="stylesheet"
      />

      {/* ── Header ── */}
      <header style={{
        borderBottom: `1px solid ${t.border}`,
        background: t.surface,
        position: "sticky", top: 0, zIndex: 100,
        boxShadow: t.shadow,
      }}>
        {/* Ligne 1 : logo + toggle */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 16px", height: 52,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo192.png" alt="Lootbar Tracker"
              style={{ width: 30, height: 30, borderRadius: 7 }}
            />
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.text, letterSpacing: -0.2 }}>Lootbar</span>
              <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}> Tracker</span>
            </div>
          </div>
          <ThemeToggle mode={mode} onToggle={toggleMode} t={t} />
        </div>

        {/* Ligne 2 : search */}
        <div style={{ padding: "0 16px 6px" }}>
          <input
            value={gameSearch}
            onChange={e => setGameSearch(e.target.value)}
            placeholder="Rechercher un jeu…"
            style={{
              background: t.surface2, border: `1px solid ${t.border}`,
              borderRadius: 6, padding: "6px 12px", color: t.text,
              fontSize: 12, outline: "none", width: "100%", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Ligne 3 : tabs scrollables filtrées */}
        {(() => {
          const q = gameSearch.toLowerCase();
          const withItems    = games.filter(g => g.item_count > 0  && g.name.toLowerCase().includes(q));
          const withoutItems = games.filter(g => g.item_count === 0 && g.name.toLowerCase().includes(q));
          return (
            <>
              <div className="game-tabs" style={{
                display: "flex", gap: 8, padding: "0 16px 6px",
                overflowX: "auto", WebkitOverflowScrolling: "touch",
              }}>
                {withItems.map(g => (
                  <button key={g.id} onClick={() => setSelectedGame(g)} style={{
                    background:   selectedGame?.id === g.id ? t.accent     : "transparent",
                    color:        selectedGame?.id === g.id ? t.accentText : t.textMuted,
                    border:      `1px solid ${selectedGame?.id === g.id ? t.accent : t.border}`,
                    borderRadius: 6, padding: "5px 12px",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                    transition: "all 0.15s", whiteSpace: "nowrap", flexShrink: 0,
                  }}>{g.name}</button>
                ))}
              </div>
              {withoutItems.length > 0 && (
                <div style={{ padding: "0 16px 8px" }}>
                  <button onClick={() => setShowEmpty(v => !v)} style={{
                    background: "transparent", border: "none", cursor: "pointer",
                    color: t.textMuted, fontSize: 11, fontFamily: "monospace",
                    padding: 0, letterSpacing: 0.5,
                  }}>
                    {showEmpty ? "▾" : "▸"} {withoutItems.length} jeux sans offres
                  </button>
                  {showEmpty && (
                    <div className="game-tabs" style={{
                      display: "flex", gap: 8, marginTop: 6,
                      overflowX: "auto", WebkitOverflowScrolling: "touch",
                    }}>
                      {withoutItems.map(g => (
                        <button key={g.id} disabled style={{
                          background: "transparent", opacity: 0.4,
                          color: t.textMuted, border: `1px solid ${t.border}`,
                          borderRadius: 6, padding: "5px 12px",
                          fontSize: 12, fontWeight: 600, cursor: "default",
                          whiteSpace: "nowrap", flexShrink: 0,
                        }}>{g.name}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </header>

      {/* ── Main ── */}
      <main style={{ padding: isMobile ? "14px 12px" : "20px 24px" }}>
        {error && (
          <div style={{
            background: t.dangerBg, border: `1px solid ${t.danger}`,
            borderRadius: 8, padding: "10px 16px", marginBottom: 16,
            color: t.danger, fontSize: 12, fontFamily: "monospace",
          }}>
            ⚠ Impossible de joindre l'API — {error}
          </div>
        )}

        {loadingItems ? (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            height: 300, color: t.textMuted, fontFamily: "monospace", fontSize: 12, letterSpacing: 1,
          }}>
            Chargement…
          </div>

        ) : isMobile ? (
          /* ── MOBILE ── */
          selectedItem ? (
            <DetailChart
              item={selectedItem}
              history={histories[selectedItem.id]}
              loading={loadingHistory && histories[selectedItem.id] === undefined}
              t={t} isMobile={true} onBack={handleBack}
              range={range} setRange={setRange}
            />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {items.map(item => (
                <PriceCard key={item.id} item={item} history={histories[item.id]}
                  onClick={handleSelectItem} selected={false} t={t}
                />
              ))}
            </div>
          )

        ) : selectedItem ? (
          /* ── DESKTOP avec item sélectionné ── */
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.7fr)",
            gap: 16, alignItems: "start",
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
              maxHeight: "calc(100vh - 112px)", overflowY: "auto", paddingRight: 2,
            }}>
              {items.map(item => (
                <PriceCard key={item.id} item={item} history={histories[item.id]}
                  onClick={handleSelectItem} selected={selectedItem?.id === item.id} t={t}
                />
              ))}
            </div>
            <div style={{ position: "sticky", top: 72 }}>
              <DetailChart
                item={selectedItem}
                history={histories[selectedItem.id]}
                loading={loadingHistory && histories[selectedItem.id] === undefined}
                t={t} isMobile={false}
                range={range} setRange={setRange}
              />
            </div>
          </div>

        ) : (
          /* ── DESKTOP grille pleine largeur ── */
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {items.map(item => (
              <PriceCard key={item.id} item={item} history={histories[item.id]}
                onClick={handleSelectItem} selected={false} t={t}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
